import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { CurrentAccountDTO } from "src/app/models/current-accounts/CurrentAccountDTO";
import { Location } from "@angular/common";
import { StatusChip, TopCard } from "src/app/models/top-card/top-card.model";
import { CurrentAccountsService } from "src/app/shared/services/current-accounts/current-accounts.service";
import { catchError, finalize, tap } from "rxjs/operators";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { TenantDataFormatDTO } from "src/app/models/Tenant/Tenant.model";
import { AppState, loadedTenantState } from "src/app/store/reducers";
import { select, Store } from "@ngrx/store";
import { KpiStatus } from "src/app/models/kpi/kpi.model";

@Component({
  selector: "app-current-accounts-detail",
  templateUrl: "./current-accounts-detail.component.html",
  styleUrls: ["./current-accounts-detail.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class CurrentAccountsDetailsComponent implements OnInit, OnDestroy {
  isLoading$ = new BehaviorSubject<boolean>(false);
  card: TopCard;

  currCode: string;
  currentAccount: CurrentAccountDTO;

  availableCredit?: number;
  totalDocNotIssuedBalance?: number;
  limitCreditParty?: number;
  amount?: number;

  loadedTenantData$: Observable<TenantDataFormatDTO>;

  //Language
  langSubscription: Subscription;

  snackbarService: any;

  currentToken: string;
  currentTenant: TenantDataFormatDTO;

  public locale: string = localStorage.getItem("locale");

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private titleService: Title,
    private location: Location,
    private store: Store<AppState>,
    private currentAccountsService: CurrentAccountsService
  ) {
    this.titleService.setTitle(this.translate.instant("CurrentAccounts.currentAccount"));
    this.loadedTenantData$ = this.store.pipe(select(loadedTenantState));

    this.setTopcard();
  }

  ngOnInit(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "ignore";
    
    this.loadLangChanges();
    this.route.params.subscribe((params) => {
      const token = params.token;
      this.loadCurrentAccountInfo(token);
    });
  }

  getStatus(value?: number, invert: boolean = false): KpiStatus {
    if (value === 0 ||  value === undefined) return KpiStatus.NEUTRAL;
    const isGood = invert ? value < 0 : value > 0;
    return isGood ? KpiStatus.SUCCESS : KpiStatus.ERROR;
  }

  loadCurrentAccountInfo(token: string) {
    this.isLoading$.next(true);
    this.currentAccountsService
      .getCurrentAccountDetail(token)
      .pipe(
        tap((currentAccount) => {
          this.currentAccount = currentAccount;
          this.loadedTenantData$.subscribe((tenant) => {
            this.currentTenant = tenant;
            this.availableCredit = currentAccount.availableCredit.toFixed(tenant?.amountDecimalPlaces);
            this.totalDocNotIssuedBalance = currentAccount.documentsNotIssuedBalance.toFixed(tenant?.amountDecimalPlaces);
            this.limitCreditParty = currentAccount.creditLimit.toFixed(tenant?.amountDecimalPlaces);
            this.amount = currentAccount.amount.toFixed(tenant?.amountDecimalPlaces);
          });
        }),
        catchError((error) => {
          this.snackbarService.show({
            message: this.translate.instant("CurrentAccounts.currentAccountsLoadError"),
            type: SnackbarType.ERROR,
          });
          return EMPTY;
        }),
        finalize(() => this.isLoading$.next(false))
      )
      .subscribe((currentAccount) => {
        this.currentToken = currentAccount.token;
        this.setTopcard(currentAccount);
      });
  }

  loadLangChanges() {
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.titleService.setTitle(this.translate.instant("CurrentAccounts.currentAccount"));
    });
  }

  refresh() {
    this.reload();
  }

  reload() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = "reload";
    this.router.navigate(["current-accounts/current-account", this.currentToken]);
  }

  back() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.location.back();
  }

  setTopcard(currentAccount?: CurrentAccountDTO) {
    this.card = {
      avatarUrl: "",
      content: "",
      imageUrl: "",
      current: currentAccount?.token,
      title: "CurrentAccounts.currentAccount",
      subtitle: currentAccount && currentAccount.party ? '[' + currentAccount?.party?.code + '] ' + currentAccount?.party?.name : null,
      status: this.setCustomStatusChip(currentAccount) ?? null,
    };
  }

  setCustomStatusChip(currentAccount: CurrentAccountDTO): StatusChip {
    if (!currentAccount || !currentAccount.party || !currentAccount.party.vatin) {
      return;
    }

    const { party: { vatin } } = currentAccount;

    return {
      value: vatin,
      style: 'background-color: rgb(209 209 209); color: rgb(109 109 109); transform: scale(0.8); margin-left: -10px;',
      icon: 'account_balance',
    };
  }

  ngOnDestroy() {
    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }
}
